/**
 * _photo_shrink.js — a phone photo redrawn small enough to post.
 *
 * A phone photo is several megabytes and nginx caps a whole request
 * (client_max_body_size, infra/nginx/chippieapp.conf), so photos are shrunk on
 * the device before they are posted. Used by photo_downscale_controller.js
 * (plain forms), and by rich_description_controller.js and
 * line_item_photo_controller.js for the photos a create form holds.
 *
 * Not a controller: esm/index.js registers only *_controller.js.
 */

// Longest side, in pixels: enough to read a site or a defect from, at a few
// hundred kilobytes.
export const MAX_EDGE = 2000;
const JPEG_QUALITY = 0.8;

/**
 * The photo redrawn within maxEdge as a JPEG, or the original when that fails
 * (a format this browser cannot decode, such as HEIC outside Safari) or would
 * be no smaller. Never rejects.
 */
export async function shrinkPhoto(file, maxEdge = MAX_EDGE) {
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
        if (!blob || blob.size >= file.size) return file;
        const name = file.name.replace(/\.[^.]*$/, "") + ".jpg";
        return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
    } catch {
        return file;
    }
}
